<!-- Base.RangeDatePicker.vue (client-lms) -->
<template>
  <div class="lms_rdp">
    <!-- Input 영역 -->
    <div ref="inputsRef" class="lms_rdp_inputs">
      <div class="lms_rdp_input" :class="{ filled: startDate, active: open, error: error }">
        <input
          type="text"
          class="lms_rdp_text"
          :value="startDate ? formatDate(startDate) : ''"
          :placeholder="placeholder[0]"
          @focus="onInputFocus"
          @blur="(e) => onInputBlur(e, 'start')"
          @keydown.enter="(e) => onInputBlur(e, 'start')"
        />
        <i class="icon_calendar_days" @click="open = !open"></i>
      </div>
      <!-- 시각(HH:mm) — withTime 일 때만 노출. 날짜와 별도 모델(startTime/endTime)로 다룬다.
           캘린더 팝업을 열지 않도록 focus 핸들러를 달지 않는다. -->
      <input
        v-if="withTime"
        type="text"
        class="lms_rdp_time"
        :class="{ filled: startTime, error: error }"
        :value="startTime"
        placeholder="00:00"
        maxlength="5"
        inputmode="numeric"
        aria-label="시작 시각"
        @focus="selectAllTime"
        @click="selectAllTime"
        @input="(e) => onTimeInput(e, 'start')"
        @blur="onTimeBlur('start')"
      />
      <span class="lms_rdp_sep">~</span>
      <div class="lms_rdp_input" :class="{ filled: endDate, active: open, error: error }">
        <input
          type="text"
          class="lms_rdp_text"
          :value="endDate ? formatDate(endDate) : ''"
          :placeholder="placeholder[1]"
          @focus="onInputFocus"
          @blur="(e) => onInputBlur(e, 'end')"
          @keydown.enter="(e) => onInputBlur(e, 'end')"
        />
        <i class="icon_calendar_days" @click="open = !open"></i>
      </div>
      <input
        v-if="withTime"
        type="text"
        class="lms_rdp_time"
        :class="{ filled: endTime, error: error }"
        :value="endTime"
        placeholder="00:00"
        maxlength="5"
        inputmode="numeric"
        aria-label="종료 시각"
        @focus="selectAllTime"
        @click="selectAllTime"
        @input="(e) => onTimeInput(e, 'end')"
        @blur="onTimeBlur('end')"
      />
    </div>
    <p v-if="error && errorMessage" class="lms_rdp_error">
      <i class="mdi mdi-alert-outline"></i>
      {{ errorMessage }}
    </p>

    <!-- 캘린더 팝업 -->
    <Teleport to="body">
      <div v-if="open" class="lms_rdp_overlay" @click="onOverlayClick">
        <div ref="popupRef" class="lms_rdp_popup" :style="popupStyle" @click.stop>
          <!-- Day View (2 months) -->
          <div v-if="viewMode === 'day'" class="lms_rdp_calendars">
            <div class="lms_rdp_month" v-for="offset in [0, 1]" :key="offset">
              <div class="lms_rdp_month_header">
                <button v-if="offset === 0" type="button" class="lms_rdp_nav" @click="prevMonth">
                  <i class="mdi mdi-chevron-left"></i>
                </button>
                <span v-else class="lms_rdp_nav_placeholder"></span>
                <button type="button" class="lms_rdp_month_title" @click="viewMode = 'month'">
                  {{ getMonthTitle(offset) }}
                </button>
                <button v-if="offset === 1" type="button" class="lms_rdp_nav" @click="nextMonth">
                  <i class="mdi mdi-chevron-right"></i>
                </button>
                <span v-else class="lms_rdp_nav_placeholder"></span>
              </div>
              <div class="lms_rdp_weekdays">
                <span v-for="(wd, i) in weekdays" :key="i" :class="{ sunday: i === 0 }">{{ wd }}</span>
              </div>
              <div class="lms_rdp_days">
                <div
                  v-for="(day, i) in getDays(offset)"
                  :key="i"
                  class="lms_rdp_day_cell"
                  :class="getDayCellClass(day)"
                >
                  <span
                    v-if="day.date"
                    class="lms_rdp_day"
                    :class="getDayClass(day)"
                    @click="onDayClick(day)"
                    @mouseenter="onDayHover(day)"
                  >{{ day.date.getDate() }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Month View -->
          <div v-else-if="viewMode === 'month'" class="lms_rdp_month" style="width: 252px">
            <div class="lms_rdp_month_header">
              <button type="button" class="lms_rdp_nav" @click="prevYear">
                <i class="mdi mdi-chevron-left"></i>
              </button>
              <button type="button" class="lms_rdp_month_title" @click="viewMode = 'year'">
                {{ baseMonth.getFullYear() }}년
              </button>
              <button type="button" class="lms_rdp_nav" @click="nextYear">
                <i class="mdi mdi-chevron-right"></i>
              </button>
            </div>
            <div class="lms_dp_grid_4">
              <button
                v-for="m in 12"
                :key="m"
                type="button"
                class="lms_dp_grid_cell"
                @click="onMonthClick(m - 1)"
              >{{ m }}월</button>
            </div>
          </div>

          <!-- Year View -->
          <div v-else class="lms_rdp_month" style="width: 252px">
            <div class="lms_rdp_month_header">
              <button type="button" class="lms_rdp_nav" @click="prevDecade">
                <i class="mdi mdi-chevron-left"></i>
              </button>
              <span class="lms_rdp_month_title_static">
                {{ decadeStart }} - {{ decadeStart + 11 }}
              </span>
              <button type="button" class="lms_rdp_nav" @click="nextDecade">
                <i class="mdi mdi-chevron-right"></i>
              </button>
            </div>
            <div class="lms_dp_grid_4">
              <button
                v-for="y in yearList"
                :key="y"
                type="button"
                class="lms_dp_grid_cell"
                @click="onYearClick(y)"
              >{{ y }}</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";

interface DayInfo {
  date: Date | null;
  inMonth: boolean;
  isSunday: boolean;
}

const props = defineProps({
  placeholder: { type: Array as () => string[], default: () => ["시작일", "종료일"] },
  error: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
  closeOnOutsideClick: { type: Boolean, default: true },
  // 기본(false)=종료일<시작일이면 자동 스왑. true=스왑 없이 입력값 그대로 둬서 부모 폼의 검증(에러)이 발동하도록.
  disableAutoSwap: { type: Boolean, default: false },
  // true=날짜 뒤에 시각(HH:mm) 입력 노출. v-model:startTime / v-model:endTime 로 값을 받는다.
  withTime: { type: Boolean, default: false },
});

const startDate = defineModel<Date | null>("start", { default: null });
const endDate = defineModel<Date | null>("end", { default: null });

// 시각은 문자열 "HH:mm" 으로 다룬다 — 날짜(Date)와 분리해야 캘린더 스왑/파싱 로직에 얽히지 않는다.
const startTime = defineModel<string>("startTime", { default: "" });
const endTime = defineModel<string>("endTime", { default: "" });

const OPEN_EVENT = "lms-range-date-picker-open";
const pickerId = Symbol("lms-range-date-picker");

const notifyOpen = () => {
  window.dispatchEvent(
    new CustomEvent(OPEN_EVENT, {
      detail: { pickerId },
    })
  );
};

const open = ref(false);
const baseMonth = ref(new Date());
const step = ref(0); // 0=시작일 선택, 1=종료일 선택
const viewMode = ref<"day" | "month" | "year">("day");

const prevYear = () => {
  const d = new Date(baseMonth.value);
  d.setFullYear(d.getFullYear() - 1);
  baseMonth.value = d;
};

const nextYear = () => {
  const d = new Date(baseMonth.value);
  d.setFullYear(d.getFullYear() + 1);
  baseMonth.value = d;
};

const decadeStart = computed(() => {
  const y = baseMonth.value.getFullYear();
  return Math.floor(y / 12) * 12;
});

const yearList = computed(() => {
  const start = decadeStart.value;
  return Array.from({ length: 12 }, (_, i) => start + i);
});

const prevDecade = () => {
  const d = new Date(baseMonth.value);
  d.setFullYear(d.getFullYear() - 12);
  baseMonth.value = d;
};

const nextDecade = () => {
  const d = new Date(baseMonth.value);
  d.setFullYear(d.getFullYear() + 12);
  baseMonth.value = d;
};

const onMonthClick = (m: number) => {
  const d = new Date(baseMonth.value);
  d.setMonth(m);
  baseMonth.value = d;
  viewMode.value = "day";
};

const onYearClick = (y: number) => {
  const d = new Date(baseMonth.value);
  d.setFullYear(y);
  baseMonth.value = d;
  viewMode.value = "month";
};
const hoverDate = ref<Date | null>(null);
const popupStyle = ref<Record<string, string>>({});

const weekdays = ["일", "월", "화", "수", "목", "금", "토"];

const formatDate = (d: Date) => {
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
};

const getMonthTitle = (offset: number) => {
  const d = new Date(baseMonth.value);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
};

const prevMonth = () => {
  const d = new Date(baseMonth.value);
  d.setMonth(d.getMonth() - 1);
  baseMonth.value = d;
};

const nextMonth = () => {
  const d = new Date(baseMonth.value);
  d.setMonth(d.getMonth() + 1);
  baseMonth.value = d;
};

const getDays = (offset: number): DayInfo[] => {
  const d = new Date(baseMonth.value);
  d.setMonth(d.getMonth() + offset);
  const year = d.getFullYear();
  const month = d.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const days: DayInfo[] = [];

  // 이전 달
  for (let i = firstDay - 1; i >= 0; i--) {
    const date = new Date(year, month - 1, daysInPrevMonth - i);
    days.push({ date, inMonth: false, isSunday: date.getDay() === 0 });
  }

  // 현재 달
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i);
    days.push({ date, inMonth: true, isSunday: date.getDay() === 0 });
  }

  // 다음 달 (6주 채우기)
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    const date = new Date(year, month + 1, i);
    days.push({ date, inMonth: false, isSunday: date.getDay() === 0 });
  }

  return days;
};

const isSameDay = (a: Date | null, b: Date | null) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isBetween = (date: Date, start: Date | null, end: Date | null) => {
  if (!start || !end) return false;
  const t = date.getTime();
  const s = start.getTime();
  const e = end.getTime();
  return t > Math.min(s, e) && t < Math.max(s, e);
};

const isInRange = (date: Date, start: Date | null, end: Date | null) => {
  if (!start || !end) return false;
  const t = date.getTime();
  const s = Math.min(start.getTime(), end.getTime());
  const e = Math.max(start.getTime(), end.getTime());
  return t >= s && t <= e;
};

const getDayCellClass = (day: DayInfo) => {
  if (!day.date) return {};
  const effectiveEnd = step.value === 1 ? (hoverDate.value || endDate.value) : endDate.value;
  const inRange = isBetween(day.date, startDate.value, effectiveEnd);
  const isStart = isSameDay(day.date, startDate.value) && effectiveEnd;
  const isEnd = isSameDay(day.date, effectiveEnd);
  const partOfRange = inRange || (isStart && !isSameDay(startDate.value, effectiveEnd)) || (isEnd && !isSameDay(startDate.value, effectiveEnd));

  // 행 경계 판단: 일요일(0)=행 시작, 토요일(6)=행 끝
  const dow = day.date.getDay();
  // 범위 내에서 이 셀이 행의 왼쪽 끝인지 (일요일이거나 range-start)
  const isRowLeft = partOfRange && (dow === 0 || isStart);
  // 범위 내에서 이 셀이 행의 오른쪽 끝인지 (토요일이거나 range-end)
  const isRowRight = partOfRange && (dow === 6 || isEnd);

  return {
    "in-range": inRange,
    "range-start": isStart,
    "range-end": isEnd,
    "range-row-left": isRowLeft,
    "range-row-right": isRowRight,
  };
};

const getDayClass = (day: DayInfo) => {
  if (!day.date) return {};
  const effectiveEnd = step.value === 1 ? (hoverDate.value || endDate.value) : endDate.value;
  return {
    "not-in-month": !day.inMonth,
    sunday: day.isSunday && day.inMonth,
    selected: isSameDay(day.date, startDate.value) || isSameDay(day.date, effectiveEnd),
    today: isSameDay(day.date, new Date()),
  };
};

const onDayClick = (day: DayInfo) => {
  if (!day.date) return;
  if (step.value === 0) {
    startDate.value = day.date;
    endDate.value = null;
    hoverDate.value = null;
    step.value = 1;
  } else {
    if (props.disableAutoSwap) {
      // 스왑 금지 — 종료일<시작일 이어도 그대로 세팅해 부모 폼 검증(에러)이 발동하도록
      endDate.value = day.date;
    } else if (day.date < startDate.value!) {
      endDate.value = startDate.value;
      startDate.value = day.date;
    } else {
      endDate.value = day.date;
    }
    step.value = 0;
    hoverDate.value = null;
    setTimeout(() => { open.value = false; }, 200);
  }
};

const parseInputDate = (str: string): Date | null => {
  // 지원 형식: 2026.01.03, 2026-01-03, 20260103
  const cleaned = str.replace(/[.\-\/]/g, "");
  if (cleaned.length !== 8 || !/^\d{8}$/.test(cleaned)) return null;
  const y = parseInt(cleaned.substring(0, 4));
  const m = parseInt(cleaned.substring(4, 6)) - 1;
  const d = parseInt(cleaned.substring(6, 8));
  const date = new Date(y, m, d);
  if (date.getFullYear() !== y || date.getMonth() !== m || date.getDate() !== d) return null;
  return date;
};

const onInputFocus = () => {
  notifyOpen();
  open.value = true;
};

const onInputBlur = (e: Event, target: "start" | "end") => {
  const input = e.target as HTMLInputElement;
  const val = input.value.trim();
  if (!val) {
    if (target === "start") startDate.value = null;
    else endDate.value = null;
    return;
  }
  const parsed = parseInputDate(val);
  if (parsed) {
    if (target === "start") {
      startDate.value = parsed;
      baseMonth.value = new Date(parsed.getFullYear(), parsed.getMonth(), 1);
    } else {
      endDate.value = parsed;
    }
    // start > end이면 swap (disableAutoSwap 이면 스왑 없이 그대로 둬서 부모 폼 검증에 맡김)
    if (!props.disableAutoSwap && startDate.value && endDate.value && startDate.value > endDate.value) {
      const tmp = startDate.value;
      startDate.value = endDate.value;
      endDate.value = tmp;
    }
  } else {
    // 잘못된 형식이면 원래 값으로 복원
    input.value = target === "start"
      ? (startDate.value ? formatDate(startDate.value) : "")
      : (endDate.value ? formatDate(endDate.value) : "");
  }
};

const onOverlayClick = () => {
  if (props.closeOnOutsideClick) {
    open.value = false;
  }
};

const onDayHover = (day: DayInfo) => {
  if (step.value === 1 && day.date) {
    hoverDate.value = day.date;
  }
};

// 팝업 위치 계산
const inputsRef = ref<HTMLElement>();
const popupRef = ref<HTMLElement>();

const onOtherPickerOpen = (event: Event) => {
  const nextPickerId = (event as CustomEvent<{ pickerId: symbol }>).detail?.pickerId;

  if (nextPickerId !== pickerId) {
    open.value = false;
  }
};

watch(open, async (val) => {
  if (val) {
    notifyOpen();
    viewMode.value = "day";
    await nextTick();
    if (inputsRef.value) {
      const rect = inputsRef.value.getBoundingClientRect();
      const gap = 4;
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      // 렌더된 팝업 실제 크기 (없으면 추정값)
      const popupH = popupRef.value?.offsetHeight ?? 360;
      const popupW = popupRef.value?.offsetWidth ?? 0;

      // 아래 공간이 부족하고 위 공간이 충분하면 위로 플립 (화면 하단 잘림 방지)
      const fitsBelow = rect.bottom + gap + popupH <= vh;
      const fitsAbove = rect.top - gap - popupH >= 0;
      const top = !fitsBelow && fitsAbove ? rect.top - popupH - gap : rect.bottom + gap;

      // 좌우 넘침 방지
      let left = rect.left;
      if (popupW && left + popupW > vw - 8) {
        left = Math.max(8, vw - popupW - 8);
      }

      popupStyle.value = {
        top: `${top}px`,
        left: `${left}px`,
      };
    }
  }
});

onMounted(() => {
  window.addEventListener(OPEN_EVENT, onOtherPickerOpen);
});

onBeforeUnmount(() => {
  window.removeEventListener(OPEN_EVENT, onOtherPickerOpen);
});

/* ── 시각(HH:mm) 입력 ── withTime 일 때만 사용 ───────────────────────── */

/** 입력 중 마스킹 — 숫자만 남기고 3자리부터 콜론을 넣는다. 부분 입력("9", "09:3")을 허용해야 타이핑이 끊기지 않는다. */
const maskTime = (raw: string): string => {
  const d = raw.replace(/\D/g, "").slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}:${d.slice(2)}`;
};

/** blur 보정 — 24시/60분 초과는 경계값으로 맞추고 2자리로 채운다. 빈값은 비운 채 둬서 부모 폼이 필수 검증. */
const normalizeTime = (raw: string): string => {
  const d = raw.replace(/\D/g, "");
  if (!d) return "";
  const h = Math.min(23, Number(d.slice(0, 2)));
  const m = Math.min(59, Number(d.slice(2, 4) || "0"));
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/**
 * 포커스/클릭 시 전체 선택 — 기본값 "00:00" 이 채워져 있어도 지우지 않고 바로 숫자를 칠 수 있게.
 * (선택 상태에서 첫 입력이 값을 대체하므로 "0930" 처럼 이어 치면 09:30 이 된다.
 *  select 없이는 기존 4자리에 새 숫자가 붙어 maskTime 이 앞 4자리만 남겨 입력이 먹지 않는다.)
 * click 에도 붙인 이유: 클릭은 focus 후 선택을 해제하므로 focus 만으로는 두 번째 클릭부터 선택이 풀린다.
 */
const selectAllTime = (e: Event) => {
  (e.target as HTMLInputElement).select();
};

const onTimeInput = (e: Event, target: "start" | "end") => {
  const input = e.target as HTMLInputElement;
  const masked = maskTime(input.value);
  input.value = masked; // 마스킹 결과를 DOM 에도 즉시 반영(모델과 같은 값이라 리렌더 충돌 없음)
  if (target === "start") startTime.value = masked;
  else endTime.value = masked;
};

const onTimeBlur = (target: "start" | "end") => {
  if (target === "start") startTime.value = normalizeTime(startTime.value);
  else endTime.value = normalizeTime(endTime.value);
};
</script>

<style scoped>
/* 시각 입력 — 날짜 입력(.lms_rdp_input, lms.css)과 같은 톤. 이 컴포넌트에서만 쓰는 신규 요소라 scoped 로 둔다. */
.lms_rdp_time {
  flex: 0 0 auto;
  width: 84px;
  height: 48px;
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
  outline: none;
}
.lms_rdp_time.filled {
  color: rgba(0, 0, 0, 0.8);
}
.lms_rdp_time:focus {
  border-color: #5c74f6;
}
.lms_rdp_time.error {
  border-color: #f93c4a;
}
.lms_rdp_time::placeholder {
  color: rgba(0, 0, 0, 0.35);
}
</style>
